// fe/src/components/LectureView.tsx

import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  useFetchLectureQuery,
  useUpdateLectureMutation,
  LectureVisibility,
} from "../gql/generated-hooks";
import { useAuth } from "../contexts/AuthContext";
import VideoPlayer from "./VideoPlayer";
import { createRouteWithParams } from "../helpers/urlHelpers";
import { ROUTES } from "../routes";
import YearNavLink from "./YearNavLink";
import Categories from "./Categories";
import Skeleton from "react-loading-skeleton";

interface LectureViewProps {
  id: string; // Prop to accept the lecture ID
}

export default function LectureView({ id }: LectureViewProps) {
  const { currentUser } = useAuth();
  const [{ data, fetching, error }] = useFetchLectureQuery({ variables: { where: { id: id } } });
  const [, executeUpdateLectureResult] = useUpdateLectureMutation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [speakers, setSpeakers] = useState("");
  const [speakersBio, setSpeakersBio] = useState("");
  const [year, setYear] = useState<number>(0);
  const [visibility, setVisibility] = useState<LectureVisibility>(LectureVisibility.Published);

  // Handle the edit button click
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Handle the save button click
  const handleSaveClick = async () => {
    setIsEditing(false);

    try {
      const updateLecture = await executeUpdateLectureResult({
        data: {
          title: { set: title },
          summary: { set: summary },
          speakers: { set: speakers },
          visibility: { set: visibility },
        },
        where: { id: id },
      });

      console.log(updateLecture); // Check the result
      alert("Lecture updated successfully"); // Show success message
    } catch (error) {
      console.error("Error updating the lecture:", error);
      alert("Error updating the lecture. Please try again."); // Show error message
    }
  };

  useEffect(() => {
    if (data?.lecture) {
      setTitle(data.lecture.title || "");
      setSummary(data.lecture.summary || "");
      setSpeakers(data.lecture.speakers || "");
      setSpeakersBio(data.lecture.speakersBio || "");
      setVisibility(data.lecture.visibility || LectureVisibility.Published);

      if (data.lecture.date) {
        const lectureDate = new Date(data.lecture.date);
        setYear(lectureDate.getFullYear());
      }
    }
  }, [data]);

  useEffect(() => {
    if (error && error.message === "[GraphQL] Unauthorized") {
      const returnUrl = encodeURIComponent(
        `${location.pathname}${location.search}${location.hash}`
      );
      navigate(`${ROUTES.LOGIN}?returnUrl=${returnUrl}`, { replace: true });
    }
  }, [error, location, navigate]);

  if (fetching) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div className="lecture-container">
      <nav className="breadcrumb">
        <NavLink to={ROUTES.HOME}>Home</NavLink> &gt; <YearNavLink to={year} />{" "}
        &gt;{" "}
        <NavLink
          to={createRouteWithParams(ROUTES.LECTURE, {
            id: data?.lecture?.id ?? "",
          })}
        >
          {title || <Skeleton />}
        </NavLink>
      </nav>
      <div>
        <h1 className="lecture-title">
          {currentUser?.isAdmin() && isEditing ? (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          ) : (
            title
          )}
        </h1>
        {currentUser?.isAdmin() && (
          <>
            <button onClick={isEditing ? handleSaveClick : handleEditClick}>
              {isEditing ? "Save" : "Edit"}
            </button>
            <div style={{ marginTop: '10px' }}>
              <label>
                Visibility:
                <select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as LectureVisibility)}
                  disabled={!isEditing}
                  style={{ marginLeft: '10px' }}
                >
                  <option value="DRAFT">Draft (Admin only)</option>
                  <option value="PUBLISHED">Published (Login required)</option>
                  <option value="UNLISTED">Unlisted (Direct link only)</option>
                  <option value="PUBLIC">Public (No login required)</option>
                </select>
              </label>
            </div>
          </>
        )}
      </div>

      <VideoPlayer videoLink={data?.lecture?.linkToVideoFile!}></VideoPlayer>
      <div className="lecture-details">
        <div className="lecture-about">
          <h2>About lecture</h2>
          <p>
            {currentUser?.isAdmin() && isEditing ? (
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
              />
            ) : (
              summary
            )}
          </p>
        </div>
        <div className="lecture-speaker">
          <h2>About {speakers}</h2>
          <p>
            {currentUser?.isAdmin() && isEditing ? (
              <textarea
                value={speakers}
                onChange={(e) => setSpeakers(e.target.value)}
              />
            ) : (
              speakersBio
            )}
          </p>
        </div>
      </div>
      <Categories metadata={data?.lecture?.metadata} />
    </div>
  );
}
